<template>
  <div
    class="asset-library"
    :class="[
      { 'asset-library--relative': isInline },
      { 'asset-library--inactive': isUploading }
    ]"
  >
    <asset-edit-modal
      v-if="isEditingAsset"
      :asset="isEditingAsset"
      @deselectAsset="deselectAsset"
    />
    <div v-if="isUploading" class="asset-library__loading-icon">
      <fa :icon="['fad', 'spinner-third']" />
    </div>
    <div class="asset-library__modal">
      <button
        v-if="!isInline"
        class="asset-library__close-btn btn btn-sm btn-dark"
        @click="$emit('close')"
      >
        <fa :icon="['fas', 'times']" size="lg" />
      </button>
      <button class="btn btn-primary" @click="showUpload">
        {{ isUploadVisible ? "Close" : "Upload Asset" }}
      </button>

      <div v-show="isUploadVisible" class="asset-library__upload my-2">
        <div class="row">
          <div class="col-4">
            <label for="assetDescription">Choose Asset File</label>
            <input
              type="file"
              name="asset"
              ref="assetInput"
              id="asset"
              @change="isUploadFileChosen = !!$refs.assetInput.files.length"
              multiple
            />
          </div>
          <div class="col-4">
            <label for="assetDescription">Asset Description</label>
            <input
              class="form-control w-100"
              id="assetDescription"
              type="text"
              v-model="assetDescription"
              placeholder="i.e. masterplan"
            />
          </div>
          <div class="col-4">
            <label>&nbsp;</label>
            <button
              class="float-right btn btn-sm btn-secondary"
              :disabled="!isUploadFileChosen"
              @click="createAsset($refs.assetInput.files)"
            >
              Upload Asset
            </button>
          </div>
        </div>
      </div>

      <div class="d-flex">
        <div class="table-filter">
          <div class="row">
            <div class="col-12">
              <label for="">Filter</label>
              <input
                class="form-control w-100"
                type="text"
                v-model="filterString"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <label for="">File Type</label>
              <select v-model="selectedType" class="form-control">
                <option value="">All</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="asset-library__categories">
                <label for="">Category</label>
                <button
                  :class="[
                    { active: selectedCategory === '' },
                    { active: hoveredCategory === 'all' }
                  ]"
                  @click="selectedCategory = ''"
                  @dragenter.prevent="hoveredCategory = 'all'"
                  @dragover.prevent
                  @dragleave.prevent="hoveredCategory = ''"
                  @drop.prevent="dropOnCategory('all')"
                >
                  <span style="pointer-events: none;">All</span>
                </button>
                <button
                  v-for="category in assetCategories"
                  :key="category"
                  :class="[
                    { active: selectedCategory === category },
                    { active: hoveredCategory === category }
                  ]"
                  @click="selectedCategory = category"
                  @dragenter.prevent="hoveredCategory = category"
                  @dragover.prevent
                  @dragleave.prevent="hoveredCategory = ''"
                  @drop.prevent="dropOnCategory(category)"
                >
                  <span style="pointer-events: none;">{{
                    transformToStartCase(category)
                  }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-wrapper">
          <table
            class="asset-table"
            :class="{
              'asset-table--asset-hover':
                isDroppingAsset && dropAssetCounter > 0
            }"
            ref="assetTable"
            @dragenter.prevent.stop="uploadDropEnter"
            @dragleave.prevent.stop="uploadDropLeave"
            @dragover.prevent.stop="uploadDropOver"
            @drop.prevent.stop="uploadDroppedAssets"
          >
            <thead>
              <tr>
                <th
                  v-if="multiple"
                  class="asset-checkbox"
                  @click="parentCheckboxToggle"
                >
                  <div
                    class="asset-checkbox__checkbox asset-checkbox__checkbox--main"
                    :class="[
                      {
                        'asset-checkbox__checkbox--checked':
                          this.selectedAssets.length > 0 &&
                          this.selectedAssets.length ===
                            this.paginatedAssets.length
                      },
                      {
                        'asset-checkbox__checkbox--partial':
                          this.selectedAssets.length > 0 &&
                          this.selectedAssets.length <
                            this.paginatedAssets.length
                      }
                    ]"
                  ></div>
                </th>
                <th v-else class="asset-checkbox"></th>
                <th @click="sortBy('description')">
                  Title
                  <fa
                    v-if="sortedType === 'description'"
                    :class="{ 'fa-rotate-180': !isSortReversed }"
                    :icon="['fad', 'sort-down']"
                  />
                </th>
                <th @click="sortBy('dimensions')" class="asset-dimensions">
                  Dimensions
                  <fa
                    v-if="sortedType === 'dimensions'"
                    :class="{ 'fa-rotate-180': !isSortReversed }"
                    :icon="['fad', 'sort-down']"
                  />
                </th>
                <th @click="sortBy('filesize')" class="asset-filesize">
                  Filesize
                  <fa
                    v-if="sortedType === 'filesize'"
                    :class="{ 'fa-rotate-180': !isSortReversed }"
                    :icon="['fad', 'sort-down']"
                  />
                </th>
                <th @click="sortBy('dateUploaded')" class="asset-date">
                  Date uploaded
                  <fa
                    v-if="sortedType === 'dateUploaded'"
                    :class="{ 'fa-rotate-180': !isSortReversed }"
                    :icon="['fad', 'sort-down']"
                  />
                </th>
                <th class="asset-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="paginatedAssets.length === 0">
                <td class="text-center">No Assets Found</td>
              </tr>
              <draggable
                tag="tr"
                :group="{ push: false, pull: false, put: false }"
                v-bind="dragOptions"
                v-model="filteredAssets"
                @start="startDrag"
              >
                <tr
                  draggable="true"
                  v-for="asset in paginatedAssets"
                  :key="asset._id"
                  :data-id="asset._id"
                  class="asset"
                  :class="[
                    { 'asset--active': isActive(asset) },
                    { 'asset--edit': isEditingAsset === asset }
                  ]"
                  ref="assetsWrapper"
                  @click="selectAsset(asset)"
                  @dblclick="quickEdit(asset)"
                >
                  <td class="asset-checkbox">
                    <div
                      class="asset-checkbox__checkbox"
                      :class="{
                        'asset-checkbox__checkbox--checked': isActive(asset)
                      }"
                    ></div>
                  </td>
                  <td>
                    <div class="asset-thumbnail">
                      <div class="asset-thumbnail__image-wrapper">
                        <img
                          class="asset-thumbnail__image"
                          :src="
                            asset.type === 'pdf'
                              ? require('../assets/img/pdf-thumb.jpg')
                              : asset.customThumbPath
                              ? $store.state.assetsPath + asset.customThumbPath
                              : $store.state.assetsPath + asset.thumbPath
                          "
                          draggable="false"
                        />
                      </div>
                      <div class="asset-thumbnail__description">
                        {{ asset.description }}
                      </div>
                    </div>
                  </td>
                  <td class="asset-dimensions">
                    {{ asset.dimensions }}
                  </td>
                  <td class="asset-filesize">
                    {{ bytesToSize(asset.filesize) }}
                  </td>
                  <td class="asset-date">
                    {{ convertDate(asset.createdAt) }}
                  </td>

                  <td class="asset-actions">
                    <div class="asset-actions__wrapper">
                      <a
                        class="btn btn-link asset-actions__button asset-actions__button--view"
                        :href="$store.state.assetsPath + asset.path"
                        target="_blank"
                        @click.stop=""
                      >
                        <fa :icon="['fas', 'external-link']" />
                      </a>
                      <button
                        v-if="isActive(asset) && !(isEditingAsset === asset)"
                        class="btn btn-link asset-actions__button"
                        @click.stop="isEditingAsset = asset"
                      >
                        <fa :icon="['fas', 'edit']" />
                      </button>
                      <button
                        class="btn btn-link asset-actions__button asset-actions__button--edit"
                        v-if="isActive(asset) && isEditingAsset === asset"
                        @click.stop="updateAsset(asset)"
                      >
                        <fa :icon="['fas', 'save']" />
                      </button>
                      <button
                        v-if="isActive(asset) && !(isEditingAsset === asset)"
                        class="btn btn-link asset-actions__button asset-actions__button--trash"
                        @click.stop="deleteAsset(asset)"
                      >
                        <fa :icon="['fas', 'trash']" />
                      </button>
                    </div>
                  </td>
                </tr>
              </draggable>
            </tbody>

            <tfoot>
              <tr>
                <td>
                  <div class="pagination">
                    <div class="pagination__item-wrapper">
                      <div
                        v-for="(page, i) in pages"
                        :key="'page' + i"
                        class="pagination__item"
                        :class="{
                          'pagination__item--active': paginationIndex === i
                        }"
                        @click="paginationIndex = i"
                      >
                        {{ i + 1 }}
                      </div>
                    </div>

                    <div class="pagination__page-size">
                      <select
                        name=""
                        id=""
                        v-model="paginationSize"
                        @change="paginationIndex = 0"
                      >
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                      </select>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>

            <div class="asset-table__popup-btn-wrapper">
              <button
                v-if="isInline"
                class="asset-table__delete-btn btn btn-danger"
                :class="{
                  'asset-table__delete-btn--active': selectedAssets.length > 1
                }"
                @click="deleteAsset()"
              >
                <fa :icon="['fas', 'trash']" />
                {{ selectedAssets.length > 1 ? selectedAssets.length : "0" }}
                Assets
              </button>
            </div>
          </table>
        </div>
      </div>

      <button
        v-if="!isInline"
        :disabled="multiple ? !selectedAssets.length : !selectedAsset"
        class="btn btn-primary mt-4 float-right"
        @click="useAsset()"
      >
        <fa :icon="['fas', 'paperclip']" />
        {{ multiple ? selectedAssets.length : "" }} Asset{{
          multiple ? "s" : ""
        }}
      </button>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import Swal from "sweetalert2";
import AssetEditModal from "@/components/AssetEditModal";
import draggable from "vuedraggable";

export default {
  props: ["multiple", "value", "isInline", "defaultCategory", "defaultType"],

  components: { AssetEditModal, draggable },

  data() {
    return {
      assetDescription: "",
      filterString: "",
      dropAssetCounter: 0,
      isDroppingAsset: false,
      isEditing: false,
      isEditingAsset: "",
      isUploadFileChosen: false,
      isUploading: false,
      isUploadVisible: false,
      selectedAsset: null,
      selectedAssets: [],
      selectedCategory: this.defaultCategory ? this.defaultCategory : "",
      selectedType: this.defaultType ? this.defaultType : "",
      isSortReversed: false,
      sortedType: "dateUploaded",

      hoveredCategory: "",
      draggingAssetId: "",

      paginationIndex: 0,
      paginationSize: 50
    };
  },

  computed: {
    assetCategories() {
      return _.orderBy(this.$store.state.assetLibrary.assetCategories);
    },

    assets() {
      let assets = _.sortBy(this.$store.state.assetLibrary.assets, asset => {
        if (this.sortedType === "dimensions") {
          const dimensions = asset.dimensions.split("x");
          if (parseInt(dimensions[0]) && parseInt(dimensions[1])) {
            return dimensions[0] * dimensions[1];
          } else {
            asset.dimensions = "n/a";
            return Number.MIN_SAFE_INTEGER;
          }
        } else {
          return asset[this.sortedType];
        }
      }).reverse();
      return this.isSortReversed ? assets.reverse() : assets;
    },

    filteredAssets: {
      get: function() {
        return _.filter(this.assets, asset => {
          if (
            this.selectedCategory === "" ||
            this.selectedCategory === asset.category
          ) {
            if (this.selectedType === "") {
              return (
                asset.description
                  .toLowerCase()
                  .includes(this.filterString.toLowerCase()) ||
                asset.path
                  .toLowerCase()
                  .includes(this.filterString.toLowerCase())
              );
            } else if (this.selectedType === asset.type) {
              return (
                asset.description
                  .toLowerCase()
                  .includes(this.filterString.toLowerCase()) ||
                asset.path
                  .toLowerCase()
                  .includes(this.filterString.toLowerCase())
              );
            }
          }
        });
      },

      set: function(newOrder) {
        return newOrder;
      }
    },

    pages() {
      return Math.ceil(this.filteredAssets.length / this.paginationSize);
    },

    paginatedAssets() {
      return this.filteredAssets.slice(
        this.paginationIndex * this.paginationSize,
        (this.paginationIndex + 1) * this.paginationSize
      );
    },

    dragOptions() {
      return {
        animation: 120,
        group: "assetList",
        disabled: false,
        ghostClass: "ghostly"
      };
    }
  },

  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateValue();
      }
    },

    pages() {
      this.paginationIndex = 0;
    }
  },

  mounted() {
    if (this.multiple) {
      this.selectedAssets = this.value;
    } else {
      if (this.value !== null && this.$refs.selectedAsset !== undefined) {
        this.selectedAsset = this.value;
        this.$nextTick(() => {
          this.$refs.assetsWrapper[0].scrollIntoView();
        });
      }
    }
  },

  methods: {
    bytesToSize(bytes) {
      if (bytes === null || bytes === undefined) {
        return "n/a";
      }

      if (bytes === 0) {
        return "0 Byte";
      }

      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

      let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return (
        Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
      );
    },

    convertDate(date) {
      let formattedDate = new Date(date);

      return moment(new Date(date)).format("DD MMM YYYY");
    },

    createAsset(files) {
      this.isUploading = true;

      this.sortBy("dateUploaded");
      this.isSortReversed = false;

      this.paginationIndex = 0;
      const tableBody = this.$refs.assetTable.querySelector("tbody");
      tableBody.scrollTop = 0;

      if (files.length > 1) {
        for (var i = 0; i < files.length; i++) {
          this.$store
            .dispatch("UPLOAD_ASSET", {
              category: this.selectedCategory ? this.selectedCategory : "misc",
              description: files[i].name,
              file: files[i]
            })
            .then(() => {
              this.isUploading = false;
            })
            .catch(err => {
              this.isUploading = false;
              console.log("error", err);
            });
        }
      } else {
        this.$store
          .dispatch("UPLOAD_ASSET", {
            category: this.selectedCategory ? this.selectedCategory : "misc",
            description: this.assetDescription
              ? this.assetDescription
              : files[0].name,
            file: files[0]
          })
          .then(() => {
            this.isUploading = false;
          })
          .catch(err => {
            this.isUploading = false;
            console.log("error", err);
          });
      }

      this.$refs.assetInput.value = "";
    },

    deleteAsset(asset) {
      const confirmText = asset
        ? "Delete asset"
        : `Delete ${this.selectedAssets.length} assets`;
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: confirmText
      }).then(result => {
        if (result.value) {
          if (asset) {
            this.$store
              .dispatch("deleteAsset", asset)
              .then(res => {
                if (res.data.errors) {
                  throw res.data.errors;
                }
                this.$notify({
                  message: `${asset.description} has been deleted`,
                  horizontalAlign: "center",
                  verticalAlign: "top",
                  type: "success"
                });
                this.selectedAssets = [];
                this.$emit("updateAssets");

                Swal.fire("Deleted!", "The asset has been deleted.", "success");
              })
              .catch(err => {
                console.log("err", err);
                if (typeof err === "object") {
                  err.forEach(error => {
                    this.$notify({
                      message: error,
                      horizontalAlign: "center",
                      verticalAlign: "top",
                      type: "warning"
                    });
                  });
                } else {
                  this.$notify({
                    message: err.message,
                    horizontalAlign: "center",
                    verticalAlign: "top",
                    type: "warning"
                  });
                }
              });
          } else {
            this.selectedAssets.forEach(asset => {
              this.$store
                .dispatch("deleteAsset", asset)
                .then(res => {
                  if (res.data.errors) {
                    throw res.data.errors;
                  }
                  this.$notify({
                    message: `${asset.description} has been deleted`,
                    horizontalAlign: "center",
                    verticalAlign: "top",
                    type: "success"
                  });
                  this.selectedAssets = [];
                  this.$emit("updateAssets");
                })
                .catch(err => {
                  if (typeof err === "object") {
                    err.forEach(error => {
                      this.$notify({
                        message: error,
                        horizontalAlign: "center",
                        verticalAlign: "top",
                        type: "warning"
                      });
                    });
                  } else {
                    this.$notify({
                      message: err.message,
                      horizontalAlign: "center",
                      verticalAlign: "top",
                      type: "warning"
                    });
                  }
                });
            });
          }
        }
      });
    },

    isActive(asset) {
      if (this.multiple) {
        let active = false;
        this.selectedAssets.forEach(selectedAsset => {
          if (selectedAsset.path === asset.path) {
            active = true;
          }
        });
        return active;
      } else {
        return this.selectedAsset
          ? this.selectedAsset.path === asset.path
          : false;
      }
    },

    parentCheckboxToggle() {
      if (this.selectedAssets.length > 0) {
        this.selectedAssets = [];
      } else if (this.selectedAssets.length === 0) {
        this.selectedAssets = _.clone(this.paginatedAssets);
      }
    },

    selectAsset(asset) {
      this.isEditingAsset = null;
      if (this.multiple) {
        if (this.isActive(asset)) {
          _.remove(this.selectedAssets, selectedAsset => {
            return selectedAsset.path === asset.path;
          });
          this.selectedAssets = JSON.parse(JSON.stringify(this.selectedAssets));
        } else {
          this.selectedAssets.push(asset);
        }
      } else {
        if (this.isActive(asset)) {
          this.selectedAsset = null;
        } else {
          this.selectedAsset = asset;
        }
      }
    },

    deselectAsset() {
      this.isEditingAsset = null;
    },

    quickEdit(asset) {
      this.selectedAssets = [];
      this.selectedAssets.push(asset);
      this.isEditingAsset = asset;
    },

    setSelectedCategory(category) {
      this.selectedCategory = category;
    },

    showUpload() {
      this.isUploadVisible = !this.isUploadVisible;
    },

    sortBy(type) {
      if (this.sortedType === type) {
        this.isSortReversed = !this.isSortReversed;
      } else {
        this.sortedType = type;
      }
    },

    transformToStartCase(string) {
      return _.startCase(_.camelCase(string));
    },

    updateAsset(asset) {
      this.isEditingAsset = null;
      this.$store.dispatch("UPDATE_ASSET", asset);
    },

    useAsset() {
      if (this.multiple) {
        this.$emit("input", this.selectedAssets);
        this.$emit("close");
      } else {
        this.$emit("input", this.selectedAsset);
        this.$emit("close");
      }
    },

    validateValue() {
      if (this.multiple) {
        return Array.isArray(this.value);
      } else {
        return typeof this.value === "string" || this.value === null;
      }
    },

    uploadDropEnter(e) {
      e.stopPropagation();
      e.preventDefault();
      this.dropAssetCounter++;
    },

    uploadDropOver(e) {
      let isFile = true;

      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (
          e.dataTransfer.items[i].type.substring(0, 5) !== "image" &&
          e.dataTransfer.items[i].type.substring(0, 5) !== "video"
        ) {
          isFile = false;
        }
      }

      if (isFile) {
        this.isDroppingAsset = true;
      }
    },

    uploadDropLeave() {
      this.dropAssetCounter--;
    },

    uploadDroppedAssets(e) {
      let isFile = true;
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        // TODO restrict allowed image / video types
        if (
          e.dataTransfer.items[i].type.substring(0, 5) !== "image" &&
          e.dataTransfer.items[i].type.substring(0, 5) !== "video"
        ) {
          isFile = false;
        }
      }

      if (isFile) {
        this.isDroppingAsset = false;
        this.createAsset(e.dataTransfer.files);
      }
    },

    dropOnCategory(cat) {
      const assetId = this.draggingAssetId;

      if (cat && assetId) {
        const asset = this.$store.state.assetLibrary.assets.find(
          asset => asset._id === assetId
        );
        asset.category = cat;

        this.$store.dispatch("UPDATE_ASSET", asset);
      }

      this.draggingAssetId = "";
      this.hoveredCategory = "";
    },

    startDrag(e) {
      this.draggingAssetId = e.item.getAttribute("data-id");
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/styles/_global.scss";
@import "@/styles/_animations.scss";
@import "@/styles/_mixins.scss";

.table-filter {
  overflow: hidden;
  overflow-x: auto;
  padding: 0 20px 0 0;
  width: 20%;
}

.table-wrapper {
  margin: 20px 0 0;
  overflow: hidden;
  overflow-x: auto;
  width: 80%;
}

.asset-table {
  border-collapse: separate;
  border-spacing: 0;
  // border: 1px solid #d4d4d4;
  // border-radius: 6px;
  overflow: hidden;
  min-width: 1000px;
  position: relative;
  width: 100%;

  tbody {
    background: #f4f5f7;
    display: block;
    max-height: none;
    overflow: auto;
    scroll-behavior: smooth;
    max-height: calc(100vh - 200px);

    @include v-breakpoint(800px) {
      max-height: calc(100vh - 500px);
    }
  }

  thead,
  tbody tr {
    display: table;
    table-layout: fixed;
    width: 100%;
  }

  thead {
    background-color: #e2e8f0;
    border-bottom: 1px solid #cbd5e0;
    border-radius: 6px 6px 0 0;
    font-size: 12px;
    text-transform: uppercase;
    top: 0;
    z-index: 10;
  }

  tbody {
    border-radius: 0 0 6px 6px;
  }

  th {
    cursor: pointer;
    user-select: none;
  }

  th,
  td {
    font-weight: 400;
    padding: 10px 15px;
  }

  tr {
    margin: 0 0 2px;

    &:first-child {
      margin-top: 14px;
    }

    &:not(:first-child):last-child {
      margin-bottom: 75px;
    }
  }

  &::before {
    background: rgba(0, 0, 0, 0);
    bottom: 0;
    content: "";
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: background 300ms;
    z-index: 100;
  }

  &::after {
    color: #ffffff;
    content: "Upload Asset";
    font-size: 40px;
    left: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -20%);
    transition: 300ms all 150ms;
    z-index: 110;
  }

  &--asset-hover {
    &::before {
      background: rgba(0, 0, 0, 0.2);
      pointer-events: auto;
    }

    &::after {
      opacity: 1;
      pointer-events: auto;
      transform: translate(-50%, -50%);
    }
  }

  &__popup-btn-wrapper {
    bottom: 20px;
    height: 38px;
    left: 20px;
    pointer-events: none;
    position: absolute;
    width: 260px;
    z-index: 10;
  }

  &__delete-btn {
    opacity: 0 !important;
    position: absolute;
    transform: translateY(100%);

    &--active {
      opacity: 1 !important;
      pointer-events: auto;
      transform: translateY(0);
    }
  }

  &__deselect-btn {
    opacity: 0 !important;
    position: absolute;
    right: 0;
    transform: translateY(100%);

    &--active {
      opacity: 1 !important;
      pointer-events: auto;
      transform: translateY(0);
    }
  }
}

.asset {
  // background-color: #fff;
  cursor: pointer;
  min-height: 60px;
  transition: background-color 100ms ease-in-out;
  user-select: none;

  &:hover {
    background-color: #cbd5e0;

    .asset-actions__wrapper {
      display: flex;
    }
  }

  &--active,
  &--active:hover {
    background-color: darken(#cbd5e0, 10%);
  }

  &--edit,
  &--edit:hover {
    background: darken(#cbd5e0, 10%);
  }
}

.asset-checkbox {
  padding: 10px 7px 10px 12px !important;
  width: 38px;

  &__checkbox {
    background: #ffffff;
    border: 1px solid rgb(178, 180, 184);

    border-radius: 50%;
    height: 15px;
    overflow: hidden;
    position: relative;
    transition: all 200ms ease-in-out;
    width: 15px;

    &::after {
      background: #4a5568;
      content: "";
      height: 100%;
      right: 50%;
      opacity: 0;
      position: absolute;
      top: 0;
      transition: all 300ms ease-in-out;
      width: 10px;
    }

    &--main {
      background: #fff;
    }

    &--checked {
      background: #4a5568;
      border: 1px solid #4a5568;
    }

    &--partial {
      &::after {
        opacity: 1;
      }
    }
  }
}

.asset-thumbnail {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 10px;

  &__image-wrapper {
    background-image: linear-gradient(
        to right,
        rgba(230, 237, 245, 0.85),
        rgba(230, 237, 245, 0.85)
      ),
      linear-gradient(to right, #000 50%, #fff 50%),
      linear-gradient(to bottom, #000 50%, #fff 50%);
    background-blend-mode: normal, difference, normal;
    background-size: 1em 1em;
    border-radius: 3px;
    flex: 0 0 120px;
    height: 80px;
    overflow: hidden;
  }

  &__image {
    object-fit: cover;
    pointer-events: none;
    user-select: none;
    width: 100%;
    height: 100%;
  }

  &__description {
    margin: 0 10px 0 0;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 260px;
    overflow: hidden;
    // -webkit-overflow-scrolling: touch;
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    padding: 2px;

    // &::-webkit-scrollbar {
    //   height: 4px;
    // }
    // &::-webkit-scrollbar-thumb {
    //   background: #5a5c60;
    //   border-radius: 3px;
    // }

    input {
      width: 100%;
    }
  }
}

.asset-label {
  font-size: 9px !important;
  font-weight: 700 !important;
  letter-spacing: 0;
  margin: 0.5em 0 0.25em 0 !important;
}

.asset-paths {
  input {
    width: 100%;
  }
}

.asset-dimensions {
  width: 200px;
}

.asset-filesize {
  width: 200px;
}

.asset-date {
  width: 200px;
}

.asset-actions {
  width: 64px;
  position: relative;
  overflow: visible;

  &__wrapper {
    position: absolute;
    right: 25px;
    top: 50%;
    transform: translateY(-50%);
    gap: 8px;
    align-items: center;
    display: none;
    justify-content: flex-end;
    white-space: nowrap;
  }

  &__button {
    border-radius: 50%;
    padding: 0 0 0 10px;

    &--trash {
      color: #e72c2c !important;

      &:hover {
        color: darken(#e72c2c, 10%) !important;
      }
    }
  }
}

// repositioning asset action button on hover
.asset:hover .asset-actions__wrapper {
  display: flex;
}
.asset:hover:not(.asset--active) .asset-actions__wrapper {
  right: auto;
  left: calc(-100% + 36px);
  top: 50%;
  transform: translateY(-50%);
}
.asset:hover:not(.asset--active)
  .asset-actions__button:not(.asset-actions__button--view) {
  display: none;
}

// .asset-table thead th:nth-child(2),
// .asset-table tbody td:nth-child(2) {
//   width: calc(100% - (38px + 200px + 200px + 200px + 70px + 180px));
//   min-width: 260px;
//   overflow: hidden;
// }

.asset-library {
  align-items: center;
  background: rgba(#000000, 0.5);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 9999;

  &--inactive {
    pointer-events: none;
    user-select: none;

    .asset-library__modal * {
      opacity: 0.8;
    }
  }

  &--relative {
    position: relative;

    .asset-library__modal {
      border-radius: 0;
      padding: 0;
      width: 100%;
    }
  }

  &__loading-icon {
    color: green;
    font-size: 80px;
    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    user-select: none;
    z-index: 10;

    svg {
      animation-name: rotate;
      animation-duration: 3s;
      animation-iteration-count: infinite;
      animation-timing-function: linear;
    }
  }

  &__categories {
    display: flex;
    flex-direction: column;

    button {
      background-color: transparent;
      border: 1px solid #eaeaea;
      border: none;
      border-radius: 0.25rem;
      color: #222;
      display: inline-block;
      font-size: 0.9rem;
      font-weight: 400;
      line-height: 1.5;
      margin: 0 0 5px;
      padding: 0.375rem 0.75rem;
      text-align: left;
      user-select: none;

      &.active {
        background-color: #e2e8f0;
      }

      &:hover {
        background-color: #e2e8f0;
      }
    }
  }

  &__heading {
    color: #252422;
    font-weight: 300;
    margin-top: 0;
  }

  &__modal {
    border-radius: 4px;
    background-color: #fff;
    padding: 2em;
    position: relative;
    width: 80vw;
  }

  &__close-btn {
    position: absolute;
    right: 20px;
  }

  &__upload {
    border: 1px solid #d4d4d4;
    border-radius: 4px;
    padding: 15px;
  }
}

.pagination {
  align-items: center;
  display: flex;
  position: relative;

  &__item-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &__item {
    align-items: center;
    border: 1px solid #808080;
    border-radius: 3px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    height: 21px;
    margin: 0 2px;
    text-align: center;
    width: 21px;

    &--active {
      background: #eaeaea;
    }
  }

  &__page-size {
    select {
      background: #eaeaea;
      border: 1px solid #808080;
      border-radius: 3px;
    }
  }
}
</style>
